import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";
import { updateUserLimits } from "src/redux/slices/authJwt";
import { dedupeInFlight } from "src/utils/staleGuard";
import { hasApiErrorCode } from "src/utils/apiErrorCode";

// Add-ons (extras recurrentes que el cliente añade a su suscripción).
// Multi-item subscription en Stripe — cada add-on es un Item dentro de la
// sub. El front gestiona quantity por add-on; el backend se ocupa de Stripe.
const initialState = {
  isLoading: false,
  error: false,
  addOns: [],     // todos los add-ons disponibles para el cliente (isActive)
  userAddOns: [], // los del usuario actual con quantity y subscriptionItemId
  // F64 · Add-ons ACTIVOS del user (paid Stripe + grants admin regalo).
  // Alimenta los gates del nav ("Contrátalo") y vistas premium.
  activeAddOnIds: [],
  activeAddOnNames: [],
  // Flag para distinguir "no hidratado" vs "hidratado sin add-ons".
  // Antes el gate leía `activeAddOnNames` (array vacío por default) y
  // pintaba el paywall antes de que llegara el fetch → mensaje falso
  // "contrata KDS" aunque estuviese contratado, sólo al F5 recargando
  // una ruta fuera de DashboardLayout (TpvCartaLayout no dispara el
  // fetch inicial de add-ons). El gate ahora espera hasta que este
  // flag pase a true antes de decidir "mostrar paywall".
  activeAddOnsLoaded: false,
  // F64 · Modal Stripe contratación (name del AddOn a contratar).
  contractDialogName: null,
};

const slice = createSlice({
  name: "addOns",
  initialState,
  reducers: {
    startLoading(state) { state.isLoading = true; },
    hasError(state, action) { state.isLoading = false; state.error = action.payload; },
    setAddOns(state, action) { state.isLoading = false; state.addOns = action.payload || []; },
    setUserAddOns(state, action) { state.userAddOns = action.payload || []; },
    setActive(state, action) {
      state.activeAddOnIds = action.payload?.ids || [];
      state.activeAddOnNames = action.payload?.names || [];
      state.activeAddOnsLoaded = true;
    },
    // F64 · Modal de contratación disparable desde cualquier CTA "Contrátalo"
    // del nav. `contractDialogName` = nombre exacto del AddOn a contratar.
    openContractDialog(state, action) { state.contractDialogName = action.payload || null; },
    closeContractDialog(state) { state.contractDialogName = null; },
    upsertUserAddOn(state, action) {
      const { addOnId, quantity } = action.payload;
      const idx = state.userAddOns.findIndex((u) => u.idAddOn === addOnId);
      if (idx >= 0) state.userAddOns[idx].quantity = quantity;
      else state.userAddOns.push({ idAddOn: addOnId, quantity });
    },
  },
});

export default slice.reducer;
export const { openContractDialog, closeContractDialog } = slice.actions;

export function getAddOns(onlyActive = true) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const res = await axios.get(`/addOn/read/${onlyActive}`);
      dispatch(slice.actions.setAddOns(res.data || []));
    } catch (err) {
      dispatch(slice.actions.hasError(err));
    }
  };
}

/**
 * F64 · Trae add-ons ACTIVOS (paid Stripe + grants admin) del user.
 *
 * 2026-08-24 · Cachea con `activeAddOnsLoaded`. Pasa `{force:true}`
 * para refetch tras contratar/cancelar un add-on. Sin este cache el
 * TPV disparaba una request en cada mount de mesa, dialog de cobro,
 * etc. (dozens/min).
 */
export function getActiveAddOns({ force = false } = {}) {
  return async (dispatch, getState) => {
    if (!force) {
      const st = getState().addOns;
      if (st?.activeAddOnsLoaded) return;
    }
    // 2026-09-11 · dedupeInFlight: al arrancar el dashboard, DashboardLayout
    // + AlertsCenter + otros gates dispatchaban getActiveAddOns() en el
    // mismo tick antes de que `activeAddOnsLoaded` estuviera true. El HAR
    // mostraba 4 GETs a /addOn/ActiveIds en 10ms. Con esta guarda todas
    // las llamadas concurrentes comparten la misma promise → 1 solo GET.
    // Un refresco forzado (tras contratar) no debe reutilizar una petición
    // en vuelo lanzada ANTES del cambio: clave propia.
    return dedupeInFlight(force ? "addOns/active:force" : "addOns/active", async () => {
      try {
        const res = await axios.get(`/addOn/ActiveIds`);
        dispatch(slice.actions.setActive(res.data || { ids: [], names: [] }));
      } catch {
        // Fallo del fetch → aún marcamos loaded para no dejar al gate
        // colgado en spinner eterno. El array vacío es la fuente de
        // verdad — sin add-ons activos.
        dispatch(slice.actions.setActive({ ids: [], names: [] }));
      }
    });
  };
}

/** F64 · Admin: lista/setea grants de un user. */
export function getUserGrants(userId) {
  return async () => {
    const res = await axios.get(`/addOn/Grants/${userId}`);
    return res.data || [];
  };
}
export function setUserGrants(userId, addOnIds) {
  return async () => {
    await axios.put(`/addOn/Grants/${userId}`, { addOnIds });
  };
}

export function createAddOn(payload) {
  return async (dispatch) => {
    try {
      await axios.post(`/addOn/create`, payload);
      dispatch(getAddOns(false));
    } catch (err) {
      dispatch(slice.actions.hasError(err));
      throw err;
    }
  };
}

export function updateAddOn(payload) {
  return async (dispatch) => {
    try {
      await axios.put(`/addOn/update`, payload);
      dispatch(getAddOns(false));
    } catch (err) { throw err; }
  };
}

export function toggleAddOnActive(id) {
  return async (dispatch) => {
    try {
      await axios.post(`/addOn/toggleActive/${id}`);
      dispatch(getAddOns(false));
    } catch (err) { throw err; }
  };
}

export function deleteAddOnPermanently(id) {
  return async (dispatch) => {
    try {
      await axios.delete(`/addOn/deletePermanently/${id}`);
      dispatch(getAddOns(false));
    } catch (err) { throw err; }
  };
}

// Cambia la cantidad contratada del add-on. Retorna AddOnChangeResultDto
// con AllowedMesas/AllowedSessions recalculados (incremento) o el detalle
// del cambio pendiente (decremento). Sincronizamos slice addOns + slice
// authJwt.user para feedback inmediato sin esperar refreshTokenData.
//
// IMPORTANTE: en decremento el backend NO baja Quantity ni AllowedMesas
// — devuelve `NewQuantity = currentQty` y `PendingQuantity = newQuantity`
// con `PendingChangeAt = current_period_end`. El front debe usar esos
// valores para mostrar el chip "Bajará a X el {fecha}".
export function setAddOnQuantity(addOnId, newQuantity) {
  return async (dispatch) => {
    let res;
    try {
      res = await axios.post(`/payment/setAddOn/${addOnId}/${newQuantity}`);
    } catch (err) {
      // Ya lo tenía contratado (caché desfasada): no es un error. El
      // backend ya invalidó su caché; refrescamos la lista activa para que
      // el nav y los AddOnGate lo desbloqueen y seguimos como éxito.
      if (newQuantity > 0 && hasApiErrorCode(err, "ADDON_ALREADY_ACTIVE")) {
        await dispatch(getActiveAddOns({ force: true }));
        return { alreadyActive: true };
      }
      throw err;
    }
    const data = res?.data || {};
    // Para el slice local usamos la quantity EFECTIVA (la que el backend
    // dice que vale ahora, no la solicitada): en decremento sigue siendo
    // la antigua, en incremento es la nueva.
    const effectiveQuantity = typeof data.newQuantity === "number"
      ? data.newQuantity
      : newQuantity;
    dispatch(slice.actions.upsertUserAddOn({ addOnId, quantity: effectiveQuantity }));
    dispatch(
      updateUserLimits({
        allowedMesas: data.allowedMesas,
        allowedSessions: data.allowedSessions,
        addOnId,
        quantity: effectiveQuantity,
        pendingQuantity: data.pendingQuantity ?? null,
        pendingChangeAt: data.pendingChangeAt ?? null,
      }),
    );
    // F75 · Refresca activeAddOnNames para que el nav y los AddOnGate
    // reaccionen INMEDIATAMENTE tras el cambio. Antes el user contrataba
    // el add-on pero el nav seguía mostrando solo la pestaña del gate
    // que se acabó de disparar (upsertUserAddOn no toca activeAddOnNames)
    // y había que recargar la página para ver el resto de items desbloqueados.
    // 2026-08-24 · force=true — el cache local está STALE tras cambio
    // de suscripción en Stripe. Sin este flag el user contrataba/anulaba
    // y el nav seguía mostrando el estado anterior. Esperamos el refresco
    // para que al volver del modal la sección ya esté desbloqueada.
    await dispatch(getActiveAddOns({ force: true }));
    return data;
  };
}

// Anula un cambio programado del add-on (PendingQuantity/PendingChangeAt).
// Cubre 3 casos:
//   • Bajada de cantidad programada en device/tables.
//   • Cancelación total programada en toggle (KDS, Reviews...).
//   • Swap de soporte programado — el backend revierte también el hermano.
// No toca Stripe (los pending solo viven en BD); post-llamada refrescamos
// user token para que Billing repinte el estado limpio sin PendingQuantity.
export function cancelPendingAddOnChange(addOnId) {
  return async (dispatch) => {
    const res = await axios.post(`/payment/cancelPendingAddOn/${addOnId}`);
    const data = res?.data || {};
    // Limpia pending del slice local sin necesidad de refetch completo.
    dispatch(
      updateUserLimits({
        allowedMesas: data.allowedMesas,
        allowedSessions: data.allowedSessions,
        addOnId,
        quantity: typeof data.newQuantity === "number" ? data.newQuantity : undefined,
        pendingQuantity: null,
        pendingChangeAt: null,
      }),
    );
    // 2026-08-24 · force=true — el cache local está STALE tras cambio
    // de suscripción en Stripe. Sin este flag el user contrataba/anulaba
    // y el nav seguía mostrando el estado anterior.
    dispatch(getActiveAddOns({ force: true }));
    return data;
  };
}
